/*
 * ============================================================
 * MEWMORA · Play Profile — six-step play-style quiz (Phase 2B)
 * ------------------------------------------------------------
 * A short, low-pressure six-step quiz that ends by suggesting a
 * play style (CHASE / POUNCE / BOND / MIXED PLAY). It is a
 * PREFERENCE guide, never a diagnosis.
 *
 * Steps are read from the DOM in order; each carries a
 * data-step-type of: name | birthday | question | toy.
 *   - name      : optional text (cat name).
 *   - birthday  : optional month + year. Never allows a future
 *                 date; derives a life stage (Kitten/Adult/Senior)
 *                 so the shopper never has to calculate an age.
 *   - question  : a required scored answer (chase/pounce/bond/
 *                 everything). These drive the result. Adding or
 *                 removing question blocks changes the count safely.
 *   - toy       : a required favorite-toy pick whose values mirror
 *                 the Choose Your Box "Favorite Toy Style" options.
 *
 * Data handoff (all additive, all guarded):
 *   - sessionStorage (this section's key) keeps the SAME shape the
 *     Play Passport and Choose Your Box already read: it always
 *     includes { index, answers[], result }. Extra profile fields
 *     (catName, birthdayMonth/Year, toy) ride along harmlessly.
 *     The `.result` key is preserved so the Passport keeps working.
 *   - MewmoraProfileStore mirrors catName, birthday, derived catAge,
 *     toyPreferences[] and the play profile for Choose Your Box to
 *     prefill later. A missing store never throws.
 *   - On completion, if a real shop URL is configured, we advance
 *     to it automatically, carrying ?profile=<key> (and preserving
 *     any existing country param — we never hardcode one). If no
 *     URL is set we simply reveal the result inline: never a broken
 *     link, never a fabricated price.
 *
 * Fully defensive: every lookup is guarded so a half-configured
 * section quietly does nothing rather than throwing.
 * ============================================================
 */
if (!customElements.get('mewmora-play-profile')) {
  customElements.define(
    'mewmora-play-profile',
    class MewmoraPlayProfile extends HTMLElement {
      constructor() {
        super();

        this.quiz = this.querySelector('[data-quiz]');
        this.resultWrap = this.querySelector('[data-result]');
        this.steps = Array.from(this.querySelectorAll('[data-pp-step]'));

        // Nothing to drive — bail out silently.
        if (!this.quiz || this.steps.length === 0) return;

        this.questionSteps = this.steps.filter(
          (s) => s.getAttribute('data-step-type') === 'question'
        );

        this.nextButton = this.querySelector('[data-quiz-next]');
        this.backButton = this.querySelector('[data-quiz-back]');
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.currentEl = this.querySelector('[data-question-current]');
        this.totalEl = this.querySelector('[data-question-total]');
        this.resultCards = Array.from(this.querySelectorAll('[data-result-card]'));
        this.shopCta = this.querySelector('[data-shop-cta]');

        // Per-step field references.
        this.nameInput = this.querySelector('[data-field="catName"]');
        this.monthSelect = this.querySelector('[data-field="birthdayMonth"]');
        this.yearSelect = this.querySelector('[data-field="birthdayYear"]');
        this.birthdayError = this.querySelector('[data-birthday-error]');
        this.notesInput = this.querySelector('[data-field="notes"]');
        this.notesCountEl = this.querySelector('[data-notes-count]');
        this.catnipTitleEl = this.querySelector('[data-catnip-title]');

        this.storageKey = this.getAttribute('data-storage-key') || 'mewmora-play-profile';

        this.currentIndex = 0;
        this.answers = new Array(this.questionSteps.length).fill(null);
        this.catName = '';
        this.birthdayMonth = '';
        this.birthdayYear = '';
        this.toy = '';
        // Catnip: '' = not answered / skipped; otherwise a stable key
        // (loves/sometimes/no_interest/not_sure). "No interest" and "Not sure"
        // are real answers distinct from the empty/skipped state.
        this.catnip = '';
        this.notes = '';
        this.savedResult = '';

        // The catnip title template (may contain [name]); captured once so we
        // can re-inject the cat's name whenever it changes.
        this.catnipTitleTemplate = this.catnipTitleEl
          ? this.catnipTitleEl.textContent.trim()
          : '';

        if (this.totalEl) this.totalEl.textContent = String(this.steps.length);

        this.bindQuestionAnswers();
        this.bindToyAnswers();
        this.bindCatnipAnswers();
        this.bindNameField();
        this.bindBirthdayFields();
        this.bindNotesField();
        this.bindSkipButtons();
        this.bindNavigation();
        this.bindResultActions();

        this.restoreState();
        this.applyEditMode();
        this.render();
      }

      /* -------------------- edit-return mode (Add Birthday) -------------------- */
      // Choose Your Box's "Add Birthday" links here with ?edit=birthday. In that
      // mode we DON'T show the result screen; we jump straight to the birthday
      // step (all other answers preserved) and, on Continue/Skip from that step,
      // return to the shop page carrying the profile — so the shopper never
      // re-does the whole quiz.
      applyEditMode() {
        let editParam = '';
        try {
          editParam = (new URLSearchParams(window.location.search).get('edit') || '').toLowerCase();
        } catch (e) {
          editParam = '';
        }
        if (editParam !== 'birthday') return;

        const birthdayIndex = this.steps.findIndex(
          (s) => s.getAttribute('data-step-type') === 'birthday'
        );
        if (birthdayIndex === -1) return;

        this.editReturnMode = true;
        // Ensure the quiz (not the result screen) is visible and land on birthday.
        if (this.resultWrap) this.resultWrap.setAttribute('hidden', '');
        if (this.quiz) this.quiz.removeAttribute('hidden');
        this.currentIndex = birthdayIndex;
      }

      /* -------------------- inputs -------------------- */
      bindQuestionAnswers() {
        this.questionSteps.forEach((step) => {
          const index = Number(step.getAttribute('data-question-index'));
          Array.from(step.querySelectorAll('[data-answer]')).forEach((input) => {
            input.addEventListener('change', () => {
              if (input.checked) {
                this.answers[index] = input.value;
                this.saveState();
                this.updateNextState();
              }
            });
          });
        });
      }

      bindToyAnswers() {
        const toyStep = this.steps.find((s) => s.getAttribute('data-step-type') === 'preferences');
        if (!toyStep) return;
        Array.from(toyStep.querySelectorAll('[data-answer]')).forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) {
              this.toy = input.value;
              this.saveState();
              this.updateNextState();
            }
          });
        });
      }

      bindCatnipAnswers() {
        const catnipStep = this.steps.find((s) => s.getAttribute('data-step-type') === 'preferences');
        if (!catnipStep) return;
        Array.from(catnipStep.querySelectorAll('[data-catnip-answer]')).forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) {
              this.catnip = input.value;
              this.saveState();
              this.updateNextState();
            }
          });
        });
      }

      bindNameField() {
        if (!this.nameInput) return;
        this.nameInput.addEventListener('input', () => {
          this.catName = (this.nameInput.value || '').trim().slice(0, 40);
          this.updateCatnipTitle();
          this.saveState();
          this.updateNextState();
        });
      }

      bindNotesField() {
        if (!this.notesInput) return;
        const onInput = () => {
          // Sanitize: strip angle brackets so no HTML/script can be injected,
          // collapse to plain text, cap at 250 chars. Stored plain; rendered
          // downstream via textContent only.
          const raw = (this.notesInput.value || '').replace(/[<>]/g, '');
          const clean = raw.slice(0, 250);
          if (clean !== this.notesInput.value) this.notesInput.value = clean;
          this.notes = clean;
          this.updateNotesCounter();
          this.saveState();
        };
        this.notesInput.addEventListener('input', onInput);
      }

      updateNotesCounter() {
        if (this.notesCountEl) {
          this.notesCountEl.textContent = String((this.notes || '').length);
        }
      }

      // Inject the cat's name into the catnip step heading ([name] token). Falls
      // back to a neutral word when no name is given, so it never shows "[name]".
      updateCatnipTitle() {
        if (!this.catnipTitleEl || !this.catnipTitleTemplate) return;
        const name = (this.catName || '').trim() || 'your cat';
        this.catnipTitleEl.textContent = this.catnipTitleTemplate.split('[name]').join(name);
      }

      // Every step can be skipped. Clear only the visible step's values, then
      // advance. A quiz with no scored answers produces no play profile.
      bindSkipButtons() {
        Array.from(this.querySelectorAll('[data-skip-current]')).forEach((btn) => {
          btn.addEventListener('click', () => {
            const step = this.steps[this.currentIndex];
            const type = step ? step.getAttribute('data-step-type') : '';
            if (type === 'name') {
              this.catName = '';
              if (this.nameInput) this.nameInput.value = '';
            } else if (type === 'birthday') {
              this.birthdayMonth = '';
              this.birthdayYear = '';
              if (this.monthSelect) this.monthSelect.value = '';
              if (this.yearSelect) this.yearSelect.value = '';
              this.validateBirthday();
            } else if (type === 'question') {
              const index = Number(step.getAttribute('data-question-index'));
              this.answers[index] = null;
              Array.from(step.querySelectorAll('[data-answer]')).forEach((input) => {
                input.checked = false;
              });
            } else if (type === 'preferences') {
              this.catnip = '';
              this.toy = '';
              this.notes = '';
              if (step) {
                Array.from(step.querySelectorAll('[data-catnip-answer]')).forEach((i) => {
                  i.checked = false;
                });
                Array.from(step.querySelectorAll('[data-answer]')).forEach((i) => {
                  i.checked = false;
                });
              }
              if (this.notesInput) this.notesInput.value = '';
              this.updateNotesCounter();
            }
            this.saveState();
            this.advance();
          });
        });
      }

      // Advance one step, or finish on the last step (shared by Next + Skip).
      // In edit-return mode, leaving the birthday step goes straight back to the
      // shop page (carrying the saved profile) rather than continuing the quiz.
      advance() {
        if (this.editReturnMode) {
          const step = this.steps[this.currentIndex];
          if (step && step.getAttribute('data-step-type') === 'birthday') {
            this.saveState();
            this.returnToShop();
            return;
          }
        }
        if (this.currentIndex < this.steps.length - 1) {
          this.currentIndex += 1;
          this.saveState();
          this.render();
        } else {
          this.finishQuiz();
        }
      }

      // Return to the shop page after an "Add Birthday" edit. Uses the saved
      // result (or recomputes) so the profile handoff is preserved, and reuses
      // buildShopUrl so country + preview_theme_id ride along. Falls back to
      // inline result if no shop URL is configured.
      returnToShop() {
        const key = this.savedResult || this.computeResult();
        const url = this.buildShopUrl(key);
        if (url) {
          try {
            window.location.assign(url);
            return;
          } catch (e) {
            /* fall through */
          }
        }
        this.showResult(key);
      }

      bindBirthdayFields() {
        const onChange = () => {
          this.birthdayMonth = this.monthSelect ? this.monthSelect.value : '';
          this.birthdayYear = this.yearSelect ? this.yearSelect.value : '';
          this.saveState();
          this.updateNextState();
        };
        if (this.monthSelect) this.monthSelect.addEventListener('change', onChange);
        if (this.yearSelect) this.yearSelect.addEventListener('change', onChange);
      }

      /* -------------------- navigation -------------------- */
      bindNavigation() {
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => {
            if (this.nextButton.getAttribute('aria-disabled') === 'true') return;
            this.advance();
          });
        }

        if (this.backButton) {
          this.backButton.addEventListener('click', () => {
            if (this.backButton.getAttribute('aria-disabled') === 'true') return;
            if (this.currentIndex > 0) {
              this.currentIndex -= 1;
              this.saveState();
              this.render();
            }
          });
        }
      }

      bindResultActions() {
        const editButton = this.querySelector('[data-edit-answers]');
        if (editButton) editButton.addEventListener('click', () => this.editAnswers());
        const clearButton = this.querySelector('[data-clear-answers]');
        if (clearButton) clearButton.addEventListener('click', () => this.clearAnswers());
      }

      /* -------------------- rendering -------------------- */
      render() {
        this.currentIndex = Math.min(Math.max(this.currentIndex, 0), this.steps.length - 1);

        this.steps.forEach((step, index) => {
          step.toggleAttribute('hidden', index !== this.currentIndex);
        });

        this.reflectCurrentStep();

        if (this.currentEl) this.currentEl.textContent = String(this.currentIndex + 1);

        // First step: Back is hidden AND disabled (no invalid "go back").
        if (this.backButton) {
          const atFirst = this.currentIndex === 0;
          this.backButton.toggleAttribute('hidden', atFirst);
          this.backButton.disabled = atFirst;
          this.backButton.setAttribute('aria-disabled', String(atFirst));
        }

        if (this.nextButton) {
          const isLast = this.currentIndex === this.steps.length - 1;
          const nextLabel = this.nextButton.getAttribute('data-next-default') || 'Continue';
          const resultLabel = this.nextButton.getAttribute('data-see-result') || 'See My Result';
          this.nextButton.textContent = isLast ? resultLabel : nextLabel;
        }

        this.updateProgress();
        this.updateNextState();
      }

      // Reflect saved values into the visible step's inputs.
      reflectCurrentStep() {
        const step = this.steps[this.currentIndex];
        if (!step) return;
        const type = step.getAttribute('data-step-type');

        if (type === 'question') {
          const index = Number(step.getAttribute('data-question-index'));
          const saved = this.answers[index];
          Array.from(step.querySelectorAll('[data-answer]')).forEach((input) => {
            input.checked = saved != null && input.value === saved;
          });
        } else if (type === 'preferences') {
          this.updateCatnipTitle();
          Array.from(step.querySelectorAll('[data-catnip-answer]')).forEach((input) => {
            input.checked = this.catnip !== '' && input.value === this.catnip;
          });
          Array.from(step.querySelectorAll('[data-answer]')).forEach((input) => {
            input.checked = this.toy !== '' && input.value === this.toy;
          });
          if (this.notesInput) this.notesInput.value = this.notes || '';
          this.updateNotesCounter();
        } else if (type === 'name') {
          if (this.nameInput) this.nameInput.value = this.catName || '';
        } else if (type === 'birthday') {
          if (this.monthSelect) this.monthSelect.value = this.birthdayMonth || '';
          if (this.yearSelect) this.yearSelect.value = this.birthdayYear || '';
          this.validateBirthday();
        }
      }

      updateProgress() {
        if (!this.progressFill) return;
        const total = this.steps.length;
        const pct = total > 0 ? Math.round(((this.currentIndex + 1) / total) * 100) : 0;
        this.progressFill.style.width = pct + '%';
      }

      // Gate the Continue button per step type. Name is optional (always OK).
      // Birthday is optional but blocks a future date. Question and toy require
      // a selection before advancing.
      updateNextState() {
        if (!this.nextButton) return;
        const step = this.steps[this.currentIndex];
        const type = step ? step.getAttribute('data-step-type') : '';
        let ok = true;

        if (type === 'question') {
          const index = Number(step.getAttribute('data-question-index'));
          ok = this.answers[index] != null; // required
        } else if (type === 'name') {
          ok = true; // optional
        } else if (type === 'birthday') {
          ok = this.validateBirthday(); // optional, but not future
        } else if (type === 'preferences') {
          ok = true; // catnip, toy and notes are all optional
        }

        this.nextButton.setAttribute('aria-disabled', String(!ok));
      }

      // Returns true when the birthday is valid to proceed (blank is valid;
      // a real past/present date is valid; a future date is not). Toggles the
      // inline error message.
      validateBirthday() {
        const m = this.birthdayMonth ? parseInt(this.birthdayMonth, 10) : 0;
        const y = this.birthdayYear ? parseInt(this.birthdayYear, 10) : 0;
        let future = false;
        if (y) {
          try {
            const now = new Date();
            const curY = now.getFullYear();
            const curM = now.getMonth() + 1; // 1-12
            if (y > curY || (y === curY && m && m > curM)) future = true;
          } catch (e) {
            future = false;
          }
        }
        if (this.birthdayError) this.birthdayError.toggleAttribute('hidden', !future);
        return !future;
      }

      // Kitten (< 1yr) / Adult / Senior (>= 11yr). Maps ONLY to the three
      // Cat Age ranges Choose Your Box already offers. Blank when no birthday.
      deriveCatAge() {
        const m = this.birthdayMonth ? parseInt(this.birthdayMonth, 10) : 0;
        const y = this.birthdayYear ? parseInt(this.birthdayYear, 10) : 0;
        if (!y) return '';
        try {
          const now = new Date();
          let years = now.getFullYear() - y;
          if (m && now.getMonth() + 1 < m) years -= 1; // birthday not reached this year
          if (years < 0) return '';
          if (years < 1) return 'Kitten';
          if (years >= 11) return 'Senior';
          return 'Adult';
        } catch (e) {
          return '';
        }
      }

      /* -------------------- scoring + result -------------------- */
      computeResult() {
        const scores = { chase: 0, pounce: 0, bond: 0 };
        let everythingCount = 0;

        this.answers.forEach((value) => {
          if (value === 'everything') {
            scores.chase += 1;
            scores.pounce += 1;
            scores.bond += 1;
            everythingCount += 1;
          } else if (value && Object.prototype.hasOwnProperty.call(scores, value)) {
            scores[value] += 1;
          }
        });

        const total = this.answers.filter((a) => a != null).length;
        // No evidence means no classification. Previously this fell through to
        // MIXED, which made an entirely skipped quiz look like a real result.
        if (total === 0) return '';

        const max = Math.max(scores.chase, scores.pounce, scores.bond);
        const leaders = ['chase', 'pounce', 'bond'].filter((k) => scores[k] === max && max > 0);

        if (leaders.length !== 1) return 'mixed';
        if (total > 0 && everythingCount === total) return 'mixed';
        return leaders[0];
      }

      // Finishing the quiz: persist everything, then EITHER auto-advance to the
      // configured shop page (carrying the profile) OR reveal the result inline.
      finishQuiz() {
        const key = this.computeResult();
        this.savedResult = key;
        this.saveState(key);

        const url = this.buildShopUrl(key);
        if (url) {
          try {
            window.location.assign(url);
            return;
          } catch (e) {
            /* fall through to inline result */
          }
        }
        this.showResult(key);
      }

      // Build the continue-to-shop URL from the REAL Liquid-rendered href only.
      // Appends ?profile=<key>, or ?profile=none when no scored answers exist,
      // and preserves existing preview/market context. The explicit "none"
      // handoff lets Choose Your Box open without inventing a MIXED profile.
      buildShopUrl(key) {
        if (!this.shopCta) return '';
        const base = this.shopCta.getAttribute('data-cta-base') || this.shopCta.getAttribute('href') || '';
        if (base === '') return '';
        this.shopCta.setAttribute('data-cta-base', base);

        // Split any existing query on the base href.
        const hashIndex = base.indexOf('#');
        const hash = hashIndex === -1 ? '' : base.slice(hashIndex);
        const noHash = hashIndex === -1 ? base : base.slice(0, hashIndex);
        const qIndex = noHash.indexOf('?');
        const path = qIndex === -1 ? noHash : noHash.slice(0, qIndex);
        const params = new URLSearchParams(qIndex === -1 ? '' : noHash.slice(qIndex + 1));

        params.set('profile', key || 'none');

        // Carry through context params already present on THIS page so the
        // handoff never drops the market test or the theme-preview session:
        //   country          — US market local test only
        //   preview_theme_id — keeps the unpublished draft-theme preview intact
        try {
          const here = new URLSearchParams(window.location.search);
          ['country', 'preview_theme_id'].forEach((p) => {
            const v = here.get(p);
            if (v && !params.has(p)) params.set(p, v);
          });
        } catch (e) {
          /* ignore */
        }

        return path + '?' + params.toString() + hash;
      }

      showResult(key) {
        const resultKey = key || this.computeResult();

        let matched = false;
        this.resultCards.forEach((card) => {
          const isMatch = card.getAttribute('data-result-card') === resultKey;
          card.toggleAttribute('hidden', !isMatch);
          if (isMatch) matched = true;
        });
        if (!matched) {
          const fallback = this.resultCards.find((c) => c.getAttribute('data-result-card') === 'mixed');
          if (fallback) fallback.removeAttribute('hidden');
        }

        if (this.quiz) this.quiz.setAttribute('hidden', '');
        if (this.resultWrap) {
          this.resultWrap.removeAttribute('hidden');
          if (typeof this.resultWrap.focus === 'function') {
            this.resultWrap.setAttribute('tabindex', '-1');
            this.resultWrap.focus({ preventScroll: false });
          }
        }

        this.decorateShopCta(resultKey);
      }

      // Keep the inline CTA (fallback view) pointing at the same profile-carrying
      // URL, so even without the auto-redirect the handoff still works.
      decorateShopCta(key) {
        if (!this.shopCta || !key) return;
        const url = this.buildShopUrl(key);
        if (url) this.shopCta.setAttribute('href', url);
      }

      editAnswers() {
        if (this.resultWrap) this.resultWrap.setAttribute('hidden', '');
        if (this.quiz) this.quiz.removeAttribute('hidden');
        // Return to the last step so a single change re-runs the result.
        this.currentIndex = this.steps.length - 1;
        this.savedResult = '';
        this.saveState('');
        this.render();
        const active = this.steps[this.currentIndex];
        if (active && typeof active.focus === 'function') {
          active.setAttribute('tabindex', '-1');
          active.focus({ preventScroll: false });
        }
      }

      clearAnswers() {
        this.answers = new Array(this.questionSteps.length).fill(null);
        this.catName = '';
        this.birthdayMonth = '';
        this.birthdayYear = '';
        this.toy = '';
        this.catnip = '';
        this.notes = '';
        this.savedResult = '';
        this.currentIndex = 0;
        if (this.nameInput) this.nameInput.value = '';
        if (this.monthSelect) this.monthSelect.value = '';
        if (this.yearSelect) this.yearSelect.value = '';
        if (this.notesInput) this.notesInput.value = '';
        this.updateNotesCounter();
        this.updateCatnipTitle();
        // Uncheck any catnip radios so a cleared quiz shows no stale selection.
        Array.from(this.querySelectorAll('[data-catnip-answer]')).forEach((i) => {
          i.checked = false;
        });
        this.resultCards.forEach((card) => card.setAttribute('hidden', ''));
        if (this.resultWrap) this.resultWrap.setAttribute('hidden', '');
        if (this.quiz) this.quiz.removeAttribute('hidden');
        try {
          window.sessionStorage.removeItem(this.storageKey);
        } catch (e) {
          /* storage unavailable — ignore */
        }
        // Clear the unified profile and every MEWMORA-owned legacy profile key
        // so old cat details cannot be restored into a future order.
        if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.clear === 'function') {
          try {
            window.MewmoraProfileStore.clear();
          } catch (e) {
            /* store unavailable — ignore */
          }
        }
        this.render();
        const first = this.steps[0];
        if (first && typeof first.focus === 'function') {
          first.setAttribute('tabindex', '-1');
          first.focus({ preventScroll: false });
        }
      }

      /* -------------------- state persistence -------------------- */
      saveState(resultKey) {
        const result = resultKey != null ? resultKey : this.savedResult || '';
        this.savedResult = result;

        // sessionStorage keeps the SHARED shape (index, answers[], result) that
        // the Passport and Choose Your Box read, plus this quiz's extra fields.
        const data = {
          index: this.currentIndex,
          answers: this.answers,
          result: result,
          catName: this.catName,
          birthdayMonth: this.birthdayMonth,
          birthdayYear: this.birthdayYear,
          toy: this.toy,
          catnip: this.catnip,
          notes: this.notes,
        };
        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (e) {
          /* storage unavailable — ignore */
        }

        // Mirror into the unified store for Choose Your Box to prefill later.
        // Additive + guarded: a missing store or write failure never throws and
        // never affects the quiz. playProfile is written ONLY once a result
        // exists, using the uppercase display value Choose Your Box uses.
        if (window.MewmoraProfileStore && typeof window.MewmoraProfileStore.update === 'function') {
          try {
            const update = {
              currentStep: this.currentIndex,
              completed: !!result,
              catName: this.catName || '',
              birthdayMonth: this.birthdayMonth || '',
              birthdayYear: this.birthdayYear || '',
              catAge: this.deriveCatAge(),
              toyPreferences: this.toy ? [this.toy] : [],
              // Catnip stored as a stable key ('' when skipped/unanswered).
              catnipPreference: this.catnip || '',
              notes: this.notes || '',
            };
            const map = { chase: 'CHASE', pounce: 'POUNCE', bond: 'BOND', mixed: 'MIXED' };
            // Always write this field. Blank explicitly clears any result from
            // an earlier attempt so an all-skipped retake cannot show stale MIXED.
            update.playProfile = result ? map[result] || '' : '';
            window.MewmoraProfileStore.update(update);
          } catch (e) {
            /* store unavailable — ignore, the quiz is unaffected */
          }
        }
      }

      restoreState() {
        let data = null;
        try {
          const raw = window.sessionStorage.getItem(this.storageKey);
          if (raw) data = JSON.parse(raw);
        } catch (e) {
          data = null;
        }
        if (!data) return;

        if (Array.isArray(data.answers)) {
          for (let i = 0; i < this.answers.length; i++) {
            this.answers[i] = data.answers[i] != null ? data.answers[i] : null;
          }
        }
        if (typeof data.catName === 'string') this.catName = data.catName;
        if (typeof data.birthdayMonth === 'string') this.birthdayMonth = data.birthdayMonth;
        if (typeof data.birthdayYear === 'string') this.birthdayYear = data.birthdayYear;
        if (typeof data.toy === 'string') this.toy = data.toy;
        if (typeof data.catnip === 'string') this.catnip = data.catnip;
        if (typeof data.notes === 'string') this.notes = data.notes;
        if (typeof data.index === 'number') {
          // Legacy seven-step sessions used different positions for Catnip and
          // Favorite Toy. Start those shoppers at the compact final step;
          // earlier saved positions still map naturally to the five-step flow.
          this.currentIndex = data.index >= this.steps.length ? this.steps.length - 1 : data.index;
        }
        this.savedResult = data.result || '';

        // Reflect restored optional fields into their inputs + counter/title so
        // returning to the quiz never shows stale UI. Guarded; no-ops if absent.
        this.updateCatnipTitle();
        this.updateNotesCounter();

        // If a result was already reached this session, show it inline (we do
        // NOT auto-redirect on restore — that would trap the shopper in a loop
        // and remove their way back).
        if (this.savedResult) {
          this.showResult(this.savedResult);
        }
      }
    }
  );
}
